import { ChangeDetectionStrategy, Component, inject, OnInit, output, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import {
  debounce,
  form,
  FormField,
  FormOptions,
  FormRoot,
  pattern,
  required,
  requiredError,
  SchemaPathTree,
  TreeValidationResult,
  validate,
  validateAsync,
} from '@angular/forms/signals';
import { NgSelectComponent } from '@ng-select/ng-select';
import { TranslatePipe } from '@ngx-translate/core';
import { of } from 'rxjs';
import { FormFieldOnErrorDirective } from 'src/app/shared/directives/form-field-on-error/form-field-on-error.directive';
import { Company } from 'src/app/shared/model/company.model';
import { Country } from 'src/app/shared/model/country.model';
import { CompanyStatus } from 'src/app/shared/model/integrator-status.enum';
import { CompanyService } from 'src/app/shared/services/company/company.service';
import { CountriesService } from 'src/app/shared/services/countries/countries.service';
import { DocumentsService } from 'src/app/shared/services/documents/documents.service';
import { LogToastService } from 'src/app/shared/services/log-toast/log-toast.service';
import { SignalFormService } from 'src/app/shared/services/signal-form/signal-form.service';
import { WebWorkerService } from 'src/app/shared/services/web-worker/web-worker.service';
import { NAME_INPUT } from 'src/app/shared/utils/utils';
import { RegistrationFormModel } from './registration-form.model';

@Component({
  selector: 'app-registration-form',
  imports: [TranslatePipe, NgSelectComponent, FormField, FormRoot, FormFieldOnErrorDirective],
  templateUrl: './registration-form.component.html',
  styles: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RegistrationFormComponent implements OnInit {
  #countriesService = inject(CountriesService);
  #logToast = inject(LogToastService);
  #webWorkerService = inject(WebWorkerService);
  #companyService = inject(CompanyService);

  success = output<Company>();
  countryList: Country[] = [];
  termsAndConditionsUrl: string;

  formModel = signal<RegistrationFormModel>({
    companyName: '',
    country: '',
    confirm: false,
  });
  formOptions: FormOptions<RegistrationFormModel> = SignalFormService.formSubmitOptions(
    this.submit.bind(this),
    this.#onInvalid.bind(this),
  );
  form = form(this.formModel, this.#formValidation.bind(this), this.formOptions);

  ngOnInit(): void {
    this.#loadCountries();
    this.#webWorkerService.initCacheWorker().cacheNdaDocument();
    this.termsAndConditionsUrl = DocumentsService.termsAndConditionsUrl;
  }

  async submit(): Promise<TreeValidationResult> {
    const model = this.formModel();
    const company: Company = {
      name: model.companyName,
      country: model.country,
      status: CompanyStatus.IN_REVIEW,
    };
    this.success.emit(company);
    return undefined;
  }

  #onInvalid(): void {
    this.form().markAsTouched();
    this.#logToast.error('MESSAGES.ERRORS.FILL_FORM_AND_TRY_AGAIN', undefined, undefined, {
      logger: false,
      toast: true,
    });
  }

  #loadCountries(): void {
    this.#countriesService.getCountries().subscribe((countries) => {
      this.countryList = countries;
    });
  }

  #formValidation(path: SchemaPathTree<RegistrationFormModel>): void {
    required(path.companyName);
    pattern(path.companyName, NAME_INPUT);
    debounce(path.companyName, 'blur');
    validateAsync(path.companyName, {
      params: (ctx) => ctx.value(),
      factory: (companyName) =>
        rxResource({
          params: companyName,
          stream: ({ params: name }: { params: string }) =>
            !name ? of(true) : this.#companyService.checkNameUnique(name),
        }),
      onSuccess: (isUnique) => (isUnique ? undefined : { kind: 'notUnique' }),
      onError: (error) => {
        this.#logToast.error('MESSAGES.ERRORS.ERROR_MAKING_REQUEST', error, undefined, {
          logger: true,
          toast: false,
        });
        return undefined;
      },
    });

    required(path.country);
    validate(path.confirm, (ctx) => (ctx.value() ? undefined : requiredError()));
  }
}
